import { Button, ProgressIndicator } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { type FC, useEffect } from "react";
import Translate from "src/ui/components/Translate/Translate";
import logo from "src/ui/assets/icons/9am-logo.svg";
import InitialLabsBloc from "./InitialLabsBloc";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";

const Container = styled.div`
  width: min(100%, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
  margin: auto;
  padding: 2rem 1rem;
  display: grid;
  height: 100%;
  justify-items: center;
  text-align: center;
  grid-template:
    "empty"
    "center"
    "bottom";
  grid-template-rows: 1fr auto 1fr;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;

  img {
    width: 68px;
    height: 68px;
  }
`;

const ReadyContainer = styled.div`
  max-width: 442px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem 1rem;
  gap: 1.5rem;
  height: 100%;
`;

const ClipboardPlaceholder = styled.div`
  background-image: url("https://cdn.sanity.io/images/g38rxyoc/production/76a7c3281e83c88cb8a42ad38bf19da3ed58ae4d-981x822.png?w=440");
  width: min(70%, 220px);
  aspect-ratio: 1.2;
  background-size: contain;
`;

const InfoText = styled.p`
  color: var(--color-text-secondary);
  font-size: 1rem;
  text-wrap: pretty;
  max-width: 340px;
  grid-area: bottom;
  display: flex;
  align-items: end;
  padding: 0 1rem 0 var(--safe-area-inset-bottom);

  @media (min-aspect-ratio: 1/1) {
    align-items: start;
  }
`;

const ReadyText = styled.h2`
  color: var(--Greys-Charcoal, #212121);
  text-wrap: pretty;
  max-width: 340px;
  text-align: center;
  font-family: var(--font-family);
  font-size: 1.2rem;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.4px;
`;

const WaitingForLabRequisitionStep: FC = () => {
  const [
    { waitingTimedOut, labOrderProviderName },
    {
      startWaitingTimeout,
      clearWaitingTimeout,
      startPolling,
      stopPolling,
      handleWaitingComplete,
      needsNotificationPermission,
      updateTasks
    }
  ] = useBlocNext(InitialLabsBloc);

  useEffect(() => {
    updateTasks();
    startWaitingTimeout();
    startPolling();
    return () => {
      clearWaitingTimeout();
      stopPolling();
    };
  }, []);

  if (waitingTimedOut) {
    return (
      <ReadyContainer>
        <ClipboardPlaceholder aria-hidden="true" />
        <ReadyText>
          <Translate msg="initialLabs.requisitionReady.title" />
        </ReadyText>
        <Button theme="sunrise" onPress={handleWaitingComplete}>
          <Translate
            msg={
              needsNotificationPermission
                ? "initialLabs.requisitionReady.turnOnNotifications"
                : "initialLabs.requisitionReady.complete"
            }
          />
        </Button>
      </ReadyContainer>
    );
  }

  return (
    <Container role="status" aria-label="Requesting lab order">
      <div
        style={{
          gridArea: "center"
        }}
      >
        <LogoContainer>
          <img src={logo} alt="" aria-hidden="true" />
        </LogoContainer>
        <ProgressIndicator
          indeterminate
          loadTime={120_000}
          label={<Translate msg="initialLabs.waitingForRequisition.title" />}
        />
      </div>
      <InfoText>
        <Translate
          msg="initialLabs.waitingForRequisition.description"
          variables={{
            labLocation: labOrderProviderName,
            context: labOrderProviderName ? "" : "generic"
          }}
        />
      </InfoText>
    </Container>
  );
};

export default WaitingForLabRequisitionStep;
